/**
 * Popup React component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import ReactDOM from "react-dom";

import FocusTrap from "focus-trap-react";

import { Key } from "ts-key-enum";

import { isBrowser, isFunction, isNonEmptyString } from "@adapptio/lib-shared";

import { IViewport, Viewport } from "../Classes/Viewport";
import { ClassList } from "../Classes/ClassList";
import { POPUP_POSITION, POPUP_POSITION_default, POPUP_POSITION_string } from "../Enums/POPUP_POSITION";
import { getStringEnumValue } from "../Functions/enumHelpers";

import { ComponentMainContext } from "../ViewComponent/ViewComponentContext";
import { IBaseProps } from "./props";

const SafeMargin = 10;

export type IPopupSize =
    | 'auto'
    | 'fit'
    | 'fit-min';

export type IPopupPosition =
    | 'auto'
    | 'fixed';

export type IPopupDirection =
    | 'vertical'
    | 'horizontal';

/**
 * Resolves offset style
 *
 * @param popupEl Target element
 * @param anchor Anchor element
 * @param width Width
 * @param viewport Viewport element
 * @param positionValue Anchor
 * @param anchorVisible Anchor visible
 */
function fixPositionStyle(
	popupEl: HTMLElement,
	anchorEl: HTMLElement,
	viewport: IViewport,
	positionValue: POPUP_POSITION
): boolean {
	const position: IPopupPosition = "auto" as IPopupPosition;
	const direction: IPopupDirection = "horizontal" as IPopupDirection;
	const safePadding = SafeMargin;
	const offset = 0;

	let _lastAnchor: POPUP_POSITION | null = null;
	let size: IPopupSize;

	if (!anchorEl || !popupEl) {
		return;
	}

	if (!anchorEl?.offsetParent === null) {
		return;
	}

	switch (positionValue) {
		case POPUP_POSITION.TOP:
		case POPUP_POSITION.BOTTOM: {
			size = 'fit';
			break;
		}
		default: {
			size = 'fit-min';
			break;
		}
	}

	const anchorRect = anchorEl.getBoundingClientRect();

	if (anchorRect.left === 0 && anchorRect.top === 0 && anchorRect.width === 0 && anchorRect.height === 0) {
		return;
	}

	const viewportRect = viewport.getRect();

	const offsetX = anchorRect.x - viewportRect.x;
	const offsetY = anchorRect.y - viewportRect.y;

	const computeAndUpdate = () => {
		let tWidth = popupEl.scrollWidth;
		let tHeight = popupEl.scrollHeight;

		let tPosition = positionValue;
		let left: number | null = null;
		let top: number | null = null;
		let right: number | null = null;
		let bottom: number | null = null;
		let width: number | null = null;
		let height: number | null = null;
		let minWidth: number | null = null;
		let minHeight: number | null = null;

		const setCoords = (_position: POPUP_POSITION) => {
			left = null;
			top = null;
			right = null;
			bottom = null;

			switch (_position) {
				case POPUP_POSITION.BOTTOM:
				case POPUP_POSITION.BOTTOM_LEFT: {
					if (direction === 'horizontal') {
						left = offsetX; //Math.max(offsetX, safePadding);
						top = Math.max(offsetY + anchorRect.height + offset, safePadding);
					} else if (direction === 'vertical') {
						right = viewportRect.width - offsetX + offset; //Math.max(viewportRect.width - offsetX + offset, safePadding);
						bottom = Math.max(
							viewportRect.height - offsetY - anchorRect.height,
							safePadding
						);
					}
					break;
				}
				case POPUP_POSITION.BOTTOM_CENTER: {
					left = Math.round(
						//Math.max(
						//	safePadding,
							Math.min(
								offsetX - (tWidth / 2 - anchorRect.width / 2),
								viewportRect.width/* - safePadding*/ - tWidth
							)
						//)
					);
					top = Math.max(offsetY + anchorRect.height + offset, safePadding);
					break;
				}
				case POPUP_POSITION.BOTTOM_RIGHT: {
					if (direction === 'horizontal') {
						right = viewportRect.width - offsetX - anchorRect.width; /*Math.max(
							viewportRect.width - offsetX - anchorRect.width,
							safePadding
						);*/
						top = Math.max(offsetY + anchorRect.height + offset, safePadding);
					} else if (direction === 'vertical') {
						left = offsetX + anchorRect.width + offset; //Math.max(offsetX + anchorRect.width + offset, safePadding);
						bottom = Math.max(
							viewportRect.height - offsetY - anchorRect.height,
							safePadding
						);
					}
					break;
				}
				case POPUP_POSITION.TOP_RIGHT: {
					if (direction === 'horizontal') {
						right = viewportRect.width - offsetX - anchorRect.width; /*Math.max(
							viewportRect.width - offsetX - anchorRect.width,
							safePadding
						);*/
						bottom = Math.max(viewportRect.height - offsetY + offset, safePadding);
					} else if (direction === 'vertical') {
						left = offsetX + anchorRect.width + offset; //Math.max(offsetX + anchorRect.width + offset, safePadding);
						top = Math.max(offsetY, safePadding);
					}
					break;
				}
				case POPUP_POSITION.TOP_CENTER: {
					left = Math.round(
						//Math.max(
						//	safePadding,
							Math.min(
								offsetX - (tWidth / 2 - anchorRect.width / 2),
								viewportRect.width/* - safePadding*/ - tWidth
							)
						//)
					);
					bottom = Math.max(viewportRect.height - offsetY + offset, safePadding);
					break;
				}
				case POPUP_POSITION.TOP:
				case POPUP_POSITION.TOP_LEFT: {
					if (direction === 'horizontal') {
						left = offsetX; // Math.max(offsetX, safePadding);
						bottom = Math.max(viewportRect.height - offsetY + offset, safePadding);
					} else if (direction === 'vertical') {
						right = viewportRect.width - offsetX + offset; // Math.max(viewportRect.width - offsetX + offset, safePadding);
						top = Math.max(offsetY, safePadding);
					}
					break;
				}
			}
		};

		// 1) Set dimensions
		if (size === 'fit') {
			if (direction === 'horizontal') {
				width = anchorRect.width;
				tWidth = width;
			} else if (direction === 'vertical') {
				height = anchorRect.height;
				tHeight = height;
			}
		} else if (size === 'fit-min') {
			if (direction === 'horizontal') {
				minWidth = anchorRect.width;
				tWidth = Math.max(tWidth, minWidth);
			} else if (direction === 'vertical') {
				minHeight = anchorRect.height;
				tHeight = Math.max(tHeight, minHeight);
			}
		}

		// 2) Calculate base position
		setCoords(positionValue);

		// 3) If position is auto, check if fits and adjust anchor
		if (position === 'auto') {
			const freeSpaceX = viewportRect.width - offsetX - anchorRect.width; // - safePadding;
			const freeSpaceY = viewportRect.height - offsetY - anchorRect.height - safePadding;
			const safeOffsetX = offsetX; // - safePadding;
			const safeOffsetY = offsetY - safePadding;

			if (left !== null && left + tWidth > viewportRect.width/* - safePadding*/) {
				if (safeOffsetX > freeSpaceX) {
					tPosition = invertPositionHorizontal(tPosition);
				}
			} else if (right !== null && right + tWidth > viewportRect.width/* - safePadding*/) {
				if (freeSpaceX > safeOffsetX) {
					tPosition = invertPositionHorizontal(tPosition);
				}
			}

			if (top !== null && top + tHeight > viewportRect.height - safePadding) {
				if (safeOffsetY > freeSpaceY) {
					tPosition = invertPositionVertical(tPosition);
				}
			} else if (bottom !== null && bottom + tHeight > viewportRect.height - safePadding) {
				if (freeSpaceY > safeOffsetY) {
					tPosition = invertPositionVertical(tPosition);
				}
			}

			if (tPosition !== positionValue) {
				setCoords(tPosition);
			}
		}

		// 4) Clamp dimensions
		if (left !== null && left + tWidth >= viewportRect.width/* - safePadding*/) {
			minWidth = null;
			width = viewportRect.width/* - safePadding*/ - left;
		} else if (right !== null && right + tWidth >= viewportRect.width/*- safePadding*/) {
			minWidth = null;
			width = viewportRect.width/* - safePadding*/ - right;
		}

		if (top !== null && top + tHeight >= viewportRect.height - safePadding) {
			minHeight = null;
			height = viewportRect.height - safePadding - top;
		} else if (bottom !== null && bottom + tHeight >= viewportRect.height - safePadding) {
			minHeight = null;
			height = viewportRect.height - safePadding - bottom;
		}

		// Assign styles
		popupEl.style.left = left !== null ? (left + viewportRect.x) + 'px' : '';
		popupEl.style.top = top !== null ? (top + viewportRect.y) + 'px' : '';
		popupEl.style.right = right !== null ? (right + (window.innerWidth - viewportRect.right)) + 'px' : '';
		popupEl.style.bottom = bottom !== null ? (bottom + (window.innerHeight - viewportRect.bottom)) + 'px' : '';

		popupEl.style.width = width !== null ? width + 'px' : '';
		popupEl.style.height = height !== null ? height + 'px' : '';
		popupEl.style.minWidth = minWidth !== null ? minWidth + 'px' : '';
		popupEl.style.minHeight = minHeight !== null ? minHeight + 'px' : '';

		if (_lastAnchor !== tPosition) {
			_lastAnchor = tPosition;
		}
	}

	computeAndUpdate();
	anchorEl.getBoundingClientRect(); // Force layout reflow
	computeAndUpdate(); // Do second pass to handle limited width/height and properly clamp it

	return true;
}

/**
 * Popup State
 */
export interface IPopupState {
	/** Popup anchor */
	anchor?: HTMLElement;

	/** Popup visibility */
	visible?: boolean;

	/** Popup width */
	width?: number;
}

/**
 * Initial Popup State
 */
export const initialPopupState: IPopupState = {
	anchor: null,
	visible: false,
	width: null
};

/**
 * Popup ref props
 */
export interface IPopupRefProps {
	/**
	 * Shows popup
	 *
	 * @param target Target anchor element
	 */
	show: (target: HTMLElement) => void;

	/**
	 * Hides popup
	 */
	hide: () => void;

	/**
	 * Toggles popup
	 */
	toggle: (target: HTMLElement) => boolean;

	/**
	 * Checks whether popup is visible
	 */
	isVisible: () => boolean;

	/**
	 * Fixes popup position
	 */
	fixPosition: () => void;

	/**
	 * Returns element
	 */
	element: HTMLDivElement;
}

/**
 * Popup props
 */
export interface IPopupProps extends IBaseProps {
	/** Position */
	position?: POPUP_POSITION_string;

	/** Role */
	role?: React.AriaRole;

	/** Show delay in milliseconds */
	showDelay?: number;

	/** Hide on Esc key press, defaults to "true" */
	hideOnEsc?: boolean;

	/** Hide on click outside of element, defaults to "true" */
	hideOnClickOutside?: boolean;

	/** Focus, defaults to "false", can be selector as well */
	focus?: boolean | string;

	/** Force anchor to be always visible (not covered by popup) */
	anchorVisible?: boolean;

	/** Children */
	children?: unknown;

	/** Key Down Capture handler */
	onKeyDownCapture?: React.KeyboardEventHandler<HTMLDivElement>;

	/** Key Up Capture handler */
	onKeyUpCapture?: React.KeyboardEventHandler<HTMLDivElement>;

	/** Show handler */
	onShow?: () => void;

	/** Hide handler */
	onHide?: () => void;

	/** Active some element parent */
	setActive?: (value: boolean) => void;
}

/**
 * Popup component
 */
export const Popup = React.forwardRef<IPopupRefProps, IPopupProps>((props, ref) => {
	const {
		position,
		role,
		showDelay = 0,
		hideOnEsc = true,
		hideOnClickOutside = true,
		focus = false,
		anchorVisible = false,
		children = null,
		componentPath,
		onKeyDownCapture,
		onKeyUpCapture,
		onShow,
		onHide,
		setActive
	} = props;

	const componentMainContext = React.useContext(ComponentMainContext);
	const { viewport } = componentMainContext;

	const [ state, setState ] = React.useReducer(
		(prevState: IPopupState, nextState: IPopupState) => ({ ...prevState, ...nextState }),
		initialPopupState
	);

	const elementRef = React.useRef<HTMLDivElement>();

	const { classList } = ClassList.getElementClassListAndIdClassName("popup", componentPath, {
		componentClassList: props.classList
	});

	classList.addModifiers({
		visible: state.visible,
		hidden: !state.visible
	});

	const positionValue = getStringEnumValue(POPUP_POSITION, position, POPUP_POSITION_default);

	const getWidthFromAnchor = React.useCallback(
		(anchor: HTMLElement) => {
			if (
				anchor &&
				(positionValue === POPUP_POSITION.TOP ||
					positionValue === POPUP_POSITION.TOP_LEFT ||
					positionValue === POPUP_POSITION.TOP_CENTER ||
					positionValue === POPUP_POSITION.TOP_RIGHT ||
					positionValue === POPUP_POSITION.BOTTOM ||
					positionValue === POPUP_POSITION.BOTTOM_LEFT ||
					positionValue === POPUP_POSITION.BOTTOM_CENTER ||
					positionValue === POPUP_POSITION.BOTTOM_RIGHT)
			) {
				const { width } = anchor.getBoundingClientRect();

				if (Number.isFinite(width)) {
					return width;
				}
			}

			return null;
		},
		[ positionValue ]
	);

	let timeout: NodeJS.Timeout;

	// Exposed methods

	const show = React.useCallback(
		(target: HTMLElement) => {
			if (timeout) {
				clearTimeout(timeout);
			}

			if (!target) {
				return;
			}

			const newState: IPopupState = {
				anchor: target,
				width: getWidthFromAnchor(target) || initialPopupState.width
			};

			if (!showDelay) {
				newState.visible = true;
			}
			// Already visible "somewhere else" - reset
			else if (state.visible) {
				newState.visible = initialPopupState.visible;
			}

			setState(newState);

			if (showDelay) {
				timeout = setTimeout(() => {
					setState({ visible: true });
				}, showDelay);
			}
		},
		[ showDelay, state.visible, timeout, getWidthFromAnchor ]
	);

	const hide = React.useCallback(() => {
		if (showDelay && timeout) {
			clearTimeout(timeout);
		}

		setState({ anchor: initialPopupState.anchor });
	}, [ showDelay, timeout ]);

	const isVisible = React.useCallback(() => state.visible, [ state.visible ]);

	const toggle = React.useCallback(
		(target: HTMLElement) => {
			if (!isVisible()) {
				show(target);

				return true;
			}

			hide();

			return false;
		},
		[ state.visible, timeout ]
	);

	const fixPosition = React.useCallback(() => {
		if (!state.anchor || !state.visible || !elementRef.current) {
			return;
		}

		if (
			!fixPositionStyle(
				elementRef.current,
				state.anchor,
				viewport,
				positionValue,
			)
		) {
			setState({ anchor: initialPopupState.anchor });
		}
	}, [
		elementRef.current,
		state.anchor,
		state.visible,
		state.width,
		viewport,
		positionValue,
		anchorVisible
	]);

	React.useImperativeHandle(ref, () => ({
		show,
		hide,
		toggle,
		isVisible,
		fixPosition,
		element: elementRef.current
	}));

	// Show popup logic

	React.useEffect(() => {
		if (!elementRef.current || !state.anchor || !state.visible) {
			return;
		}

		fixPosition();

		elementRef.current.style.opacity = "1";

		function _windowEditorViewportUpdateHandler() {
			const anchorWidth = getWidthFromAnchor(state.anchor);

			if (anchorWidth) {
				setState({ width: anchorWidth });
			}

			fixPosition();
		}

		function _windowEditorViewportScrollHandler() {
			fixPosition();
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		function _windowComponentContainerScrollHandler(event: any) {
			const { idClassName } = event.detail;

			if (!state.anchor) {
				return;
			}

			if (!idClassName || !!state.anchor.closest(`.${idClassName}`)) {
				hide();
				// fixPosition(); // buggy
			}
		}

		window.addEventListener(Viewport.UPDATE_EVENT_TYPE, _windowEditorViewportUpdateHandler);
		window.addEventListener(Viewport.SCROLL_EVENT_TYPE, _windowEditorViewportScrollHandler);
		window.addEventListener("adpt_component_container_scroll", _windowComponentContainerScrollHandler);

		let _windowKeyDownHandler: (event: KeyboardEvent) => void;
		let _windowClickHandler: (event: MouseEvent) => void;

		if (hideOnEsc) {
			_windowKeyDownHandler = (event) => {
				if (event.key === Key.Escape) {
					hide();
				}
			};

			window.addEventListener("keydown", _windowKeyDownHandler);
		}

		if (hideOnClickOutside) {
			_windowClickHandler = (event) => {
				const { target } = event;

				if (
					target instanceof Element &&
					!elementRef.current.contains(target) &&
					!state.anchor.contains(target)
				) {
					hide();
					if (isFunction(setActive)) {
						setActive(false);
					}
				}
			};

			window.addEventListener("click", _windowClickHandler);
		}

		if (isFunction(onShow)) {
			onShow();
		}

		return () => {
			window.removeEventListener(Viewport.UPDATE_EVENT_TYPE, _windowEditorViewportUpdateHandler);
			window.removeEventListener(Viewport.SCROLL_EVENT_TYPE, _windowEditorViewportScrollHandler);
			window.removeEventListener(
				"adpt_component_container_scroll",
				_windowComponentContainerScrollHandler
			);

			if (_windowKeyDownHandler) {
				window.removeEventListener("keydown", _windowKeyDownHandler);
			}

			if (_windowClickHandler) {
				window.removeEventListener("click", _windowClickHandler);
			}
		};
	}, [ state.visible, onShow, viewport, positionValue, getWidthFromAnchor, fixPosition ]); // state.anchor omitted on purpose

	// Hide popup logic

	React.useEffect(() => {
		if (state.anchor || !state.visible) {
			return;
		}

		setState({ visible: initialPopupState.visible });

		if (isFunction(onHide)) {
			onHide();
		}
	}, [ state.anchor, onHide ]); // state.visible omitted on purpose

	const visible = state.visible && isBrowser();

	React.useEffect(() => {
		if (
			elementRef.current &&
			visible &&
			isNonEmptyString(focus) &&
			(!document.activeElement || !elementRef.current.contains(document.activeElement))
		) {
			const target = elementRef.current.querySelector(focus);

			if (target instanceof HTMLElement && isFunction(target.focus)) {
				target.focus();
			}
		}
	}, [ elementRef.current, visible ]);

	let element = (
		<div
			ref={elementRef}
			role={role}
			className={classList.toClassName()}
			tabIndex={-1}
			aria-modal={true}
			onKeyDownCapture={onKeyDownCapture}
			onKeyUpCapture={onKeyUpCapture}
		>
			{focus && visible ? (
				<button type="button" className="popup__focus-button" onClick={() => hide()} />
			) : null}
			<div className="popup__content">{children}</div>
		</div>
	);

	if (focus && visible) {
		element = (
			<FocusTrap
				active={focus && visible}
				focusTrapOptions={{
					escapeDeactivates: hideOnEsc,
					clickOutsideDeactivates: hideOnClickOutside,
					allowOutsideClick: true
				}}
			>
				{element}
			</FocusTrap>
		);
	}

	return visible ? ReactDOM.createPortal(element, viewport.getRootElement()) : element;
});

function invertPositionHorizontal(anchor: POPUP_POSITION): POPUP_POSITION {
	switch (anchor) {
		case POPUP_POSITION.TOP_LEFT: {
			return POPUP_POSITION.TOP_RIGHT;
		}
		case POPUP_POSITION.TOP_RIGHT: {
			return POPUP_POSITION.TOP_LEFT	;
		}
		case POPUP_POSITION.BOTTOM_LEFT: {
			return POPUP_POSITION.BOTTOM_RIGHT;
		}
		case POPUP_POSITION.BOTTOM_RIGHT: {
			return POPUP_POSITION.BOTTOM_LEFT;
		}
		default: {
			return anchor;
		}
	}
}

function invertPositionVertical(anchor: POPUP_POSITION): POPUP_POSITION {
	switch (anchor) {
		case POPUP_POSITION.TOP: {
			return POPUP_POSITION.BOTTOM;
		}
		case POPUP_POSITION.TOP_LEFT: {
			return POPUP_POSITION.BOTTOM_LEFT;
		}
		case POPUP_POSITION.TOP_CENTER: {
			return POPUP_POSITION.BOTTOM_CENTER
		}
		case POPUP_POSITION.TOP_RIGHT: {
			return POPUP_POSITION.BOTTOM_RIGHT;
		}
		case POPUP_POSITION.BOTTOM: {
			return POPUP_POSITION.TOP;
		}
		case POPUP_POSITION.BOTTOM_LEFT: {
			return POPUP_POSITION.TOP_LEFT;
		}
		case POPUP_POSITION.BOTTOM_CENTER: {
			return POPUP_POSITION.TOP_CENTER;
		}
		case POPUP_POSITION.BOTTOM_RIGHT: {
			return POPUP_POSITION.TOP_RIGHT;
		}
		default: {
			return anchor;
		}
	}
}
